import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product, Category, ProductImage } from '../types';
import * as storage from '../services/storageService';

interface StoreContextType {
  products: Product[];
  categories: Category[];
  activeProduct: Product | null;
  isLoadingData: boolean;
  addProduct: (product: Product) => Promise<void>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (productId: string) => void;
  addCategory: (name: string) => void;
  setActiveProduct: (product: Product | null) => void;
  saveGeneratedAsset: (imageUrl: string, type: 'generated' | 'edited' | 'video', style?: string) => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [loadedProducts, loadedCategories] = await Promise.all([
          storage.getProducts(),
          storage.getCategories()
        ]);
        setProducts(loadedProducts);
        setCategories(loadedCategories);
      } catch (error) {
        console.error("Failed to load data", error);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, []);

  const addProduct = async (product: Product) => {
    setProducts(prev => [product, ...prev]);
    const saved = await storage.saveProduct(product);
    setProducts(prev => prev.map(p => p.id === saved.id ? saved : p));
  };

  const updateProduct = async (product: Product) => {
    setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    const saved = await storage.saveProduct(product);
    setProducts(prev => prev.map(p => p.id === saved.id ? saved : p));
    if (activeProduct && activeProduct.id === saved.id) {
        setActiveProduct(saved);
    }
  };

  const deleteProduct = async (productId: string) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
    if (activeProduct?.id === productId) setActiveProduct(null);
    await storage.deleteProduct(productId);
  };

  const addCategory = (name: string) => {
    const newCategory: Category = {
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name
    };
    if (!categories.find(c => c.id === newCategory.id)) {
      setCategories(prev => [...prev, newCategory]);
      storage.saveCategory(newCategory);
    }
  };

  const saveGeneratedAsset = async (imageUrl: string, type: 'generated' | 'edited' | 'video', style?: string) => {
    if (activeProduct) {
      const newImage: ProductImage = {
        id: Date.now().toString(),
        url: imageUrl,
        type,
        style,
        createdAt: Date.now()
      };
      
      const updated = {
        ...activeProduct,
        images: [newImage, ...activeProduct.images],
        updatedAt: Date.now()
      };
      await updateProduct(updated);
      alert('Asset saved to product gallery');
    } else {
      const newProduct: Product = {
        id: Date.now().toString(),
        name: type === 'video' ? `Cinema Session ${new Date().toLocaleTimeString()}` : `Studio Session ${new Date().toLocaleTimeString()}`,
        description: 'Generated in Virtual Studio',
        categoryIds: [],
        tags: ['generated', type],
        images: [{
          id: Date.now().toString(),
          url: imageUrl,
          type,
          style,
          createdAt: Date.now()
        }],
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await addProduct(newProduct);
      alert('Saved as new product in Catalog');
    }
  };

  return (
    <StoreContext.Provider value={{
      products,
      categories,
      activeProduct,
      isLoadingData,
      addProduct,
      updateProduct,
      deleteProduct,
      addCategory,
      setActiveProduct,
      saveGeneratedAsset
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};
